/**
 * Gallery Page - Photo gallery and carousel display
 */

import React, { useState, useEffect } from 'react';
import PageLayout from '../components/PageLayout';
import PageHeader from '../components/PageHeader';
import Section from '../components/Section';
import Card from '../components/Card';
import Button from '../components/Button';
import LoadingState from '../components/LoadingState';
import GalleryCarousel from '../components/gallery/GalleryCarousel';
import galleryApi from '../services/galleryApi';
import { AlertTriangle, Info } from 'lucide-react';

const Gallery = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [images, setImages] = useState([]);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    fetchGalleryData();
  }, []);

  const fetchGalleryData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await galleryApi.getCarouselData();
      setImages(response.data.images || []);
      setSettings(response.data.settings || {});
    } catch (err) {
      console.error('Error fetching gallery data:', err);
      setError('Failed to load gallery. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    fetchGalleryData();
  };

  if (loading) {
    return (
      <PageLayout maxWidth="1400px">
        <PageHeader
          title="Gallery"
          subtitle="Club Photos & Moments"
        />
        <Section>
          <Card level={1}>
            <LoadingState message="Loading gallery..." />
          </Card>
        </Section>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout maxWidth="1400px">
        <PageHeader
          title="Gallery"
          subtitle="Club Photos & Moments"
        />
        <Section>
          <Card level={1}>
            <div style={{
              padding: 'var(--spacing-3xl) var(--spacing-lg)',
              textAlign: 'center'
            }}>
              <AlertTriangle 
                size={60} 
                style={{
                  color: 'var(--color-error)',
                  marginBottom: 'var(--spacing-lg)'
                }} 
              />
              <h3 style={{
                fontSize: 'var(--font-size-2xl)',
                fontWeight: 'var(--font-weight-semibold)',
                margin: '0 0 var(--spacing-sm) 0',
                color: 'var(--color-text)'
              }}>Error Loading Gallery</h3>
              <p style={{
                fontSize: 'var(--font-size-base)',
                color: 'var(--color-text-secondary)',
                margin: '0 0 var(--spacing-lg) 0'
              }}>{error}</p>
              <Button onClick={handleRetry} variant="primary">
                Try Again
              </Button>
            </div>
          </Card>
        </Section>
      </PageLayout>
    );
  }

  return (
    <PageLayout maxWidth="1400px">
      <PageHeader
        title="Gallery"
        subtitle={
          images.length === 0
            ? 'No photos available'
            : `${images.length} ${images.length === 1 ? 'photo' : 'photos'}`
        }
      />

      <Section>
        <Card level={1}>
          <GalleryCarousel images={images} settings={settings} />

          {images.length > 0 && (
            <div style={{ marginTop: 'var(--spacing-2xl)' }}>
              <div style={{
                background: 'var(--color-background-light)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--spacing-lg)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--spacing-md)',
                boxShadow: 'var(--shadow-sm)'
              }}>
                <Info 
                  size={24} 
                  style={{
                    color: 'var(--color-primary)',
                    flexShrink: 0,
                    marginTop: 'var(--spacing-xs)'
                  }} 
                />
                <div style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-text-secondary)',
                  lineHeight: 1.6
                }}>
                  <strong style={{
                    color: 'var(--color-text)',
                    fontWeight: 'var(--font-weight-semibold)'
                  }}>Navigation:</strong> Use arrow keys or click the arrows to navigate.
                  {settings.auto_advance && (
                    <span style={{
                      display: 'block',
                      marginTop: 'var(--spacing-xs)'
                    }}> Images will auto-advance every {settings.carousel_interval / 1000} seconds.</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </Card>
      </Section>
    </PageLayout>
  );
};

export default Gallery;
